import type { ContainerProps } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useAppConfig } from "@/contexts/AppConfigContext";
import { useFeatureFlags } from "@/contexts/FeaturesContext";
import { analytics, TRACKING_CONSENT_RESET_EVENT } from "@/utils/analytics";
import { isStaticPagePath } from "@/utils/routes";
import CookieBanner from "./CookieBanner";

interface CookieConsentControllerProps {
  inlineMaxWidth?: ContainerProps["maxWidth"];
}

export default function CookieConsentController({ inlineMaxWidth }: CookieConsentControllerProps) {
  const { flags } = useFeatureFlags();
  const { usesExternalConsent } = useAppConfig();
  const location = useLocation();
  const previousDisplay = useRef(flags.cookieBanner);
  const [dismissed, setDismissed] = useState(() => analytics.getTrackingConsent() !== null);
  const enabled = !usesExternalConsent && flags.cookieBanner !== "hidden";
  const consentRequired = usesExternalConsent || enabled;
  const hideModalOnStaticPage =
    flags.cookieBanner === "modal" && isStaticPagePath(location.pathname);

  useEffect(() => {
    analytics.configureConsentRequirement(consentRequired);
    analytics.initialize();

    if (enabled && previousDisplay.current === "hidden") setDismissed(false);
    previousDisplay.current = flags.cookieBanner;
  }, [consentRequired, enabled, flags.cookieBanner]);

  useEffect(() => {
    analytics.page(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const handleConsentReset = () => {
      if (flags.cookieBanner !== "hidden") setDismissed(false);
    };
    window.addEventListener(TRACKING_CONSENT_RESET_EVENT, handleConsentReset);
    return () => window.removeEventListener(TRACKING_CONSENT_RESET_EVENT, handleConsentReset);
  }, [flags.cookieBanner]);

  const handleOnlyNecessary = () => {
    analytics.setTrackingConsent("denied");
    setDismissed(true);
  };

  const handleAcceptAll = () => {
    analytics.setTrackingConsent("granted");
    analytics.page(location.pathname);
    setDismissed(true);
  };

  if (!enabled || dismissed || hideModalOnStaticPage) return null;

  return (
    <CookieBanner
      display={flags.cookieBanner}
      inlineMaxWidth={inlineMaxWidth}
      onOnlyNecessary={handleOnlyNecessary}
      onAcceptAll={handleAcceptAll}
    />
  );
}
